import React, { useEffect, useState } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, ShieldCheck, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Wordmark } from "@/components/Brand";
import { Field } from "@/components/form";

const DEMO_EMAIL = "demo@nexstays.live";
const DEMO_PHONE = "9876543210";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[6-9]\d{9}$/;
type Channel = "email" | "whatsapp";
const PATHS: Record<Channel, { req: string; verify: string; key: string }> = {
  email: { req: "/auth/otp/request", verify: "/auth/otp/verify", key: "email" },
  whatsapp: { req: "/auth/whatsapp/request", verify: "/auth/whatsapp/verify", key: "phone" },
};

function WhatsAppStep({ phone, setPhone, onSent, enabled }: { phone: string; setPhone: (p: string) => void; onSent: (demo: boolean) => void; enabled: boolean }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(PATHS.whatsapp.req, { phone });
      onSent(r.data.demo);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (PHONE_RE.test(phone)) send(); }}>
      <Field label={t("whatsapp_number")}>
        <div className="flex items-center h-14 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-emerald-600">
          <span className="px-4 text-slate-500 font-semibold border-r border-slate-200">+91</span>
          <input data-testid="login-phone-input" inputMode="numeric" autoComplete="tel-national" maxLength={10} value={phone} autoFocus
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} placeholder="98XXXXXXXX"
            className="flex-1 h-full px-4 bg-transparent outline-none text-lg font-mono-num tracking-wider" />
        </div>
      </Field>
      {!enabled && <p data-testid="login-whatsapp-unavailable" className="text-xs rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">{t("whatsapp_soon")}</p>}
      <Button type="submit" data-testid="login-send-whatsapp-btn" disabled={!PHONE_RE.test(phone) || busy} className="w-full h-14 rounded-xl text-base bg-[#25D366] hover:bg-[#1ebe5b] text-white">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><MessageCircle className="h-5 w-5 mr-2" />{t("send_whatsapp_code")}</>}
      </Button>
      <p className="text-[11px] text-slate-400">{t("demo_phone_hint")} {DEMO_PHONE}</p>
    </form>
  );
}

function EmailStep({ email, setEmail, onSent }: { email: string; setEmail: (e: string) => void; onSent: (demo: boolean) => void }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const send = async (e = email) => {
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { email: e.trim() });
      onSent(r.data.demo);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (EMAIL_RE.test(email.trim())) send(); }}>
      <Field label={t("email_label")}>
        <div className="flex items-center h-14 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-emerald-600">
          <Mail className="ml-4 h-5 w-5 text-slate-400" />
          <input
            data-testid="login-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 h-full px-3 bg-transparent outline-none text-base"
          />
        </div>
      </Field>
      <Button type="submit" data-testid="login-send-otp-btn" disabled={!EMAIL_RE.test(email.trim()) || busy} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("send_otp")}
      </Button>
      <button
        type="button"
        data-testid="login-demo-btn"
        onClick={() => { setEmail(DEMO_EMAIL); send(DEMO_EMAIL); }}
        className="w-full h-12 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/60 text-emerald-800 text-sm font-semibold flex items-center justify-center gap-2 pg-tap"
      >
        <Sparkles className="h-4 w-4" /> {t("demo_account")}
      </button>
    </form>
  );
}

function OtpStep({ channel, target, demo, onBack }: { channel: Channel; target: string; demo: boolean; onBack: () => void }) {
  const { t } = useT();
  const { login } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(30);
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);
  const verify = async (c = code) => {
    setBusy(true);
    try {
      const r = await api.post(PATHS[channel].verify, { [PATHS[channel].key]: target, code: c });
      login(r.data.token, r.data.owner);
    } catch (e) {
      toast.error(errMsg(e));
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    try {
      await api.post(PATHS[channel].req, { [PATHS[channel].key]: target });
      setTimer(30);
      toast.success(t("otp_resent"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        {t("otp_sent_to")} <b data-testid="login-otp-email" className="break-all">{channel === "whatsapp" ? `WhatsApp +91 ${target}` : target}</b>{" "}
        <button data-testid="login-change-email-btn" onClick={onBack} className="text-emerald-700 font-semibold underline underline-offset-2">{t(channel === "whatsapp" ? "change_number" : "change_email")}</button>
      </p>
      <Field label={t("otp_label")}>
        <InputOTP data-testid="login-otp-input" maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} autoFocus>
          <InputOTPGroup className="gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} className="h-14 w-12 rounded-xl border text-xl font-bold bg-white" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </Field>
      {demo ? (
        <p data-testid="login-demo-otp-hint" className="text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">{t("demo_otp_hint")}</p>
      ) : (
        <p data-testid="login-inbox-hint" className="text-xs text-slate-500">{t(channel === "whatsapp" ? "check_whatsapp" : "check_inbox")}</p>
      )}
      <Button data-testid="login-verify-btn" disabled={code.length !== 6 || busy} onClick={() => verify()} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("verify_login")}
      </Button>
      <button data-testid="login-resend-btn" disabled={timer > 0} onClick={resend} className="w-full text-sm font-semibold text-slate-600 disabled:text-slate-400">
        {timer > 0 ? `${t("resend_in")} ${timer}s` : t("resend_otp")}
      </button>
    </div>
  );
}

export default function Login() {
  const { t } = useT();
  const [channel, setChannel] = useState<Channel>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [waEnabled, setWaEnabled] = useState(true);
  const [sent, setSent] = useState<null | { demo: boolean }>(null);
  useEffect(() => { api.get("/auth/channels").then((r) => setWaEnabled(r.data.whatsapp)).catch(() => {}); }, []);
  const tabs: { id: Channel; label: string; Icon: any }[] = [{ id: "email", label: t("email"), Icon: Mail }, { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle }];
  return (
    <div className="min-h-screen bg-[#F6F7F5]">
      <div className="max-w-md mx-auto px-5 pt-6 pb-10">
        <Wordmark size={40} />
        <div className="mt-14 pg-rise">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">{t("login_title")}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-slate-900">{t("tagline")}</h1>
          <p className="mt-3 text-slate-500 text-sm">Beds · Tenants · Rent · Receipts · Menu</p>
        </div>
        <div className="mt-10 rounded-3xl bg-white border border-slate-200/80 p-5 shadow-sm pg-rise">
          {sent ? (
            <OtpStep channel={channel} target={channel === "email" ? email.trim().toLowerCase() : phone} demo={sent.demo} onBack={() => setSent(null)} />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 mb-5" data-testid="login-channel-tabs">
                {tabs.map(({ id, label, Icon }) => (
                  <button key={id} type="button" data-testid={`login-channel-${id}`} onClick={() => setChannel(id)}
                    className={`h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${channel === id ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                    <Icon className="h-4 w-4" />{label}
                  </button>
                ))}
              </div>
              {channel === "email" ? (
                <EmailStep email={email} setEmail={setEmail} onSent={(demo) => setSent({ demo })} />
              ) : (
                <WhatsAppStep phone={phone} setPhone={setPhone} enabled={waEnabled} onSent={(demo) => setSent({ demo })} />
              )}
            </>
          )}
        </div>
        <p className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 text-emerald-700" /> {t("login_footer")}
        </p>
        <a href="/resident/login" data-testid="owner-resident-link" className="mt-3 inline-block text-sm font-semibold text-[#131e2e] underline underline-offset-2">{t("resident_link")}</a>
      </div>
    </div>
  );
}
